import React from "react";
import { Link } from "react-router-dom";
import LandingNav from "@/components/landing/LandingNav";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { ArrowLeft } from "lucide-react";
import useSeo from "@/hooks/useSeo";

/**
 * Shared scaffolding for static marketing / policy pages (About, Privacy,
 * Terms, Trust documents, etc.). Renders the marketing nav + footer and a
 * centred long-form article column. Also wires per-page SEO metadata via
 * the `useSeo` hook so each route gets a real <title>, description, and
 * OG/Twitter card automatically.
 *
 * Props:
 *   eyebrow:         short label shown above the title  (e.g. "Policy")
 *   title:           main page title (also used as <title>)
 *   subtitle:        one-line subtitle under the title (also used as description)
 *   lastUpdated:     display string for "Last updated …" (omit to hide)
 *   path:            canonical pathname for og:url / canonical (e.g. "/privacy")
 *   seoTitle:        optional override for browser/social title
 *   seoDescription:  optional override for the meta description
 *   children:        article body. Wrap sections in <PolicySection title="…">.
 */
export default function MarketingPageLayout({
  eyebrow,
  title,
  subtitle,
  lastUpdated,
  path,
  seoTitle,
  seoDescription,
  children,
}) {
  useSeo({
    title: seoTitle || title,
    description: seoDescription || subtitle,
    path,
    type: "article",
  });
  return (
    <div className="bg-white text-zinc-900 antialiased min-h-screen flex flex-col" data-testid="marketing-page">
      <LandingNav onDemo={() => { /* No-op on policy pages; CTAs link to /register. */ }} />
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20 pb-20">
          <Link to="/" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-900 mb-6">
            <ArrowLeft className="w-3 h-3" /> Back to GuestIntel
          </Link>
          {eyebrow && <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-3">{eyebrow}</div>}
          <h1 className="font-display font-black tracking-tight text-4xl lg:text-6xl leading-[1.05]">{title}</h1>
          {subtitle && <p className="mt-4 text-lg text-zinc-700">{subtitle}</p>}
          {lastUpdated && (
            <p className="mt-4 text-xs text-zinc-500" data-testid="marketing-page-last-updated">
              Last updated · {lastUpdated}
            </p>
          )}
          <div className="mt-10 max-w-none text-zinc-700 space-y-8 leading-relaxed [&_a]:text-teal [&_a:hover]:underline [&_strong]:text-zinc-900 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-1.5 [&_p]:my-3">
            {children}
          </div>
          <div className="mt-16 text-sm text-zinc-500 border-t border-zinc-200 pt-6">
            Questions? Email <a href="mailto:trust@guestintel.net" className="text-teal hover:underline">trust@guestintel.net</a>{" "}or visit our{" "}
            <Link to="/trust" className="text-teal hover:underline">Trust Center</Link>.
          </div>
        </div>
      </main>
      <LandingFooter />
    </div>
  );
}

/**
 * Lightweight heading + body block. Wrap each policy section in this so the
 * Tailwind `prose` styling is applied consistently across all pages.
 */
export function PolicySection({ title, children }) {
  return (
    <section className="space-y-3">
      <h2 className="font-display font-black tracking-tight text-2xl text-zinc-900 mt-2">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
